import {
  PermissionCode,
  type FleetDamageReport,
  type FleetVehicleDetail,
  type UpdateFleetDamageStatusRequest,
} from '@gb-ticket/shared-types';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import {
  FLEET_DAMAGE_KIND_LABELS,
  FLEET_DAMAGE_SEVERITY_LABELS,
  FLEET_DAMAGE_STATUS_LABELS,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import { fleetTableFrameClassName } from '@/products/fleet/features/fleet-panel';
import { FleetDocumentDeleteButton } from '@/products/fleet/features/fleet-document-delete-button';
import {
  fleetDocumentDownloadHref,
  useFleetVehicleDamages,
  useReleaseFleetVehicle,
  useUpdateFleetDamageStatus,
} from '@/products/fleet/features/use-fleet';
import { Link } from 'react-router';
import { FLEET_REPORTS_PATH } from '@/products/fleet/fleet-paths';

const NEXT_STATUS: Record<
  FleetDamageReport['status'],
  UpdateFleetDamageStatusRequest['status'][] | null
> = {
  reported: ['triaged', 'rejected'],
  triaged: ['in_workshop', 'done', 'rejected'],
  in_workshop: ['done', 'rejected'],
  done: null,
  rejected: null,
};

export function FleetVehicleDamagesTab({
  vehicle,
  manage,
}: {
  vehicle: FleetVehicleDetail;
  manage: boolean;
}) {
  const me = useMe();
  const canManage = manage || hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const documentsManage = hasPermission(me.data, PermissionCode.FLEET_DOCUMENTS_MANAGE);
  const damages = useFleetVehicleDamages(vehicle.id);
  const updateStatus = useUpdateFleetDamageStatus();
  const release = useReleaseFleetVehicle(vehicle.id);

  const items = damages.data?.items ?? [];
  const holdingOpen = items.some(
    (row) => row.severity === 'not_drivable' && row.status !== 'done' && row.status !== 'rejected',
  );

  async function setStatus(id: string, status: UpdateFleetDamageStatusRequest['status']) {
    try {
      await updateStatus.mutateAsync({ id, body: { status } });
      toast.success('Status gespeichert.');
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Status konnte nicht gespeichert werden.';
      toast.error(message);
    }
  }

  async function onRelease() {
    try {
      await release.mutateAsync();
      toast.success('Fahrzeug freigegeben.');
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.message : 'Freigabe nicht möglich.';
      toast.error(message);
    }
  }

  return (
    <div className="space-y-4">
      {canManage && vehicle.operationalStatus === 'out_of_service' ? (
        <Button
          type="button"
          disabled={damages.isPending || holdingOpen || release.isPending}
          onClick={onRelease}
        >
          Fahrzeug freigeben
        </Button>
      ) : null}
      {holdingOpen && !damages.isPending && vehicle.operationalStatus === 'out_of_service' ? (
        <p className="text-sm text-muted-foreground">
          Freigabe erst möglich, wenn alle Meldungen mit „Nicht fahrbereit“ erledigt sind.
        </p>
      ) : null}
      {damages.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {items.length === 0 && !damages.isPending ? (
        <p className="text-sm text-muted-foreground">Keine Meldungen für dieses Fahrzeug.</p>
      ) : null}
      <ul className={`${fleetTableFrameClassName} divide-y`}>
        {items.map((row) => {
          const next = canManage ? NEXT_STATUS[row.status] : null;
          return (
            <li key={row.id} className="space-y-2 p-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-medium">
                    {FLEET_DAMAGE_KIND_LABELS[row.kind]} ·{' '}
                    {FLEET_DAMAGE_SEVERITY_LABELS[row.severity]}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {FLEET_DAMAGE_STATUS_LABELS[row.status]} · {formatDeDateTime(row.createdAt)}
                  </p>
                  <p className="mt-2 text-sm">{row.description}</p>
                </div>
                {canManage ? (
                  <Link
                    to={`${FLEET_REPORTS_PATH}?meldung=${row.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    In Meldungen öffnen
                  </Link>
                ) : null}
              </div>
              {row.photos && row.photos.length > 0 ? (
                <ul className="space-y-2">
                  {row.photos.map((photo) => (
                    <li
                      key={photo.id}
                      className="flex flex-wrap items-center justify-between gap-2"
                    >
                      <span className="text-sm">{photo.fileName}</span>
                      <span className="flex flex-wrap items-center gap-3">
                        <a
                          href={fleetDocumentDownloadHref(photo.id)}
                          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Herunterladen
                        </a>
                        {canManage || documentsManage || me.data?.id === row.reportedById ? (
                          <FleetDocumentDeleteButton
                            documentId={photo.id}
                            fileName={photo.fileName}
                            subject="an dieser Meldung"
                          />
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {next && next.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {next.map((status) => (
                    <Button
                      key={status}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={updateStatus.isPending}
                      onClick={() => setStatus(row.id, status)}
                    >
                      {FLEET_DAMAGE_STATUS_LABELS[status]}
                    </Button>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
